import React, { useState } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import { useQuery } from "@apollo/client/react";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import AssetImage from "src/components/wizard/Item/AssetImage";
import { GET_SHIPMENT_QUERY } from "../../../constants/graphql/queries";
import getTotalReceivingUnitsLabel from "../../capture/ReceivingWizard/Shipment/TotalReceivingUnits/getTotalReceivingUnitsLabel";
import ItemId from "src/views/shopping/Orders/ItemId";
import ShipmentErrorModal from "./ShipmentErrorModal";
import ItemNotFoundModal from "./ItemNotFoundModal";
import InventoryTransactionMultipleModal from "src/components/modals/InventoryTransactionMultipleModal/index";
import MobileLocationWarning from "./MobileLocationWarning";
import SelectMobileLocationModal from "src/views/capture/RackLocations/StorageLocationAutocomplete/SelectMobileLocationModal";
import { getAssetWithOverrideMobileLocation } from "./utils";
import MoveActions from "./MoveActions";
import MoveConfirmationModal from "./MoveConfirmationModal";
import { formatRackLocationLabel } from "src/utils/rackLocations";

export const ReceiverInventoryMoveContext = React.createContext();

export default function ReceiverInventoryMove({ code, onClose }) {
  const [autoSelect, setAutoSelect] = useState(null);
  const [shipmentId, itemId] = code?.split(",") || [];
  const [openInventoryModal, setOpenInventoryModal] = useState(false);
  const [openMobileLocationModal, setOpenMobileLocationModal] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [newLocationNames, setNewLocationNames] = useState([]);
  const [warehouseName, setWarehouseName] = useState("");
  const [mobileLocation, setMobileLocation] = useState(null);
  const [overrideRackLocation, setOverrideRackLocation] = useState(null);
  const { loading, error, data } = useQuery(GET_SHIPMENT_QUERY, {
    variables: { id: shipmentId },
    skip: !shipmentId,
  });

  const handleConsolidateMobileLocation = () => {
    setOpenMobileLocationModal(true);
  };

  const handleMobileLocationClose = () => {
    setOpenMobileLocationModal(false);
  };

  const handleMobileLocationOverride = mobileWarehouseLocation => {
    setOverrideRackLocation({
      mobile_warehouse_location: {
        id: mobileWarehouseLocation.id,
        location_id: mobileWarehouseLocation.locationId,
        location_name: mobileWarehouseLocation.locationName,
      },
    });
    setOpenMobileLocationModal(false);
    setOpenInventoryModal(true);
  };

  const handleMobileLocationSubmit = (value, mobileWarehouseLocation) => {
    const [{ locationName } = {}] = value || [];
    setOverrideRackLocation({
      mobile_warehouse_location: {
        id: mobileWarehouseLocation.id,
        location_id: mobileWarehouseLocation.locationId,
        location_name: mobileWarehouseLocation.locationName,
      },
    });
    setOpenMobileLocationModal(false);
    setOpenInventoryModal(true);
    setAutoSelect(locationName);
  };

  const handleCloseInventoryModal = () => {
    setOpenInventoryModal(false);
    setAutoSelect(null);
  };

  const handleInventoryMoveSuccess = responseData => {
    const assetData = responseData?.asset;
    const { rackLocations = [], warehousesFromRackLocations = [] } = assetData;
    const names = rackLocations.flatMap(rl => 
      (rl.warehouse_locations || []).map(loc => loc.location_name)
    ).filter(Boolean);
    const firstWarehouse = warehousesFromRackLocations[0]?.name || "";
    const mobileLoc = rackLocations.find(rl => rl.mobile_warehouse_location)?.mobile_warehouse_location;
    
    setNewLocationNames(names);
    setWarehouseName(firstWarehouse);
    setMobileLocation(mobileLoc);
    setOpenInventoryModal(false);
    setConfirmationOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmationOpen(false);
    onClose();
  };

  const handleMoveToOtherWarehouse = ([location]) => {
    const newLocation = { 
      warehouse: location.warehouse,
      warehouse_location: location
    };
    if (location.mobile_warehouse_location?.id) {
      newLocation.mobile_warehouse_location = location.mobile_warehouse_location;
    }
    setOverrideRackLocation(newLocation);
    setOpenInventoryModal(true);
    setAutoSelect(location.locationName);
  };

  if (loading) {
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return <ShipmentErrorModal onClose={onClose} errorMessage={error.message} />;
  }

  const shipment = data?.shipment;
  const item = shipment?.shipmentItems?.find(item => item?.asset?.itemId === itemId) || {};
  const asset = getAssetWithOverrideMobileLocation(item.asset, overrideRackLocation || {});

  const autoSelectLocations = autoSelect ? { warehouse_locations: [{ location_name: autoSelect }] } : null;

  if (!shipment || !item.asset) {
    return <ItemNotFoundModal onClose={onClose} />;
  }

  const hasMobileLocation = item.asset?.rackLocations?.some(
    location => location.mobile_warehouse_location?.id
  );

  if (openInventoryModal) {
    return (
      <ReceiverInventoryMoveContext.Provider value={{ autoSelectLocations }}>
        <InventoryTransactionMultipleModal
          title="Confirm New Locations"
          displayPalletBreakdown={false}
          displayQuantityCalculation={false}
          displayNewAvailableQuantity={false}
          displayTotalQuantityChange={false}
          displayShipmentId={false}
          displayAddAnotherLocation={false}
          displayBulkActions={false}
          open={true}
          onClose={handleCloseInventoryModal}
          onSuccess={handleInventoryMoveSuccess}
          asset={asset}
          openMoveAssistantModal
          submitLabel="Confirm Locations"
          shipmentItem={item}
          shipment={shipment}
          actionSource="inventory_move"
        />
      </ReceiverInventoryMoveContext.Provider>
    );
  }

  if (confirmationOpen) {
    return (
      <MoveConfirmationModal
        open={confirmationOpen}
        onClose={handleCloseConfirmationModal}
        locations={newLocationNames}
        warehouseName={warehouseName}
        mobileLocation={mobileLocation}
      />
    );
  }

  return (
    <>
      <Dialog open maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "center" }}>
          <Typography fontSize={24} fontWeight={500} color="textPrimary">
            Receiver Inventory Move
          </Typography>
          <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center">Image</TableCell>
                <TableCell align="center">Item ID</TableCell>
                {asset?.barcode && <TableCell align="center">Barcode</TableCell>}
                <TableCell align="center">Description</TableCell>
                <TableCell align="center">Quantity Received</TableCell>
                {item.totalPieces && item.totalPieces.length > 0 && (
                  <TableCell align="center">Handling Units Received</TableCell>
                )}
                <TableCell align="center">Existing Location</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell align="center">
                  <AssetImage width={100} height={90} image={item?.attachments[0]?.url} />
                </TableCell>
                <TableCell align="center">
                  <ItemId asset={asset} />
                </TableCell>
                {asset?.barcode && <TableCell align="center">{asset.barcode}</TableCell>}
                <TableCell align="center">{asset?.description}</TableCell>
                <TableCell align="center">{item.quantity}</TableCell>
                {item.totalPieces && item.totalPieces.length > 0 && (
                  <TableCell align="center">
                    {getTotalReceivingUnitsLabel(item.totalPieces)}
                  </TableCell>
                )}
                <TableCell align="center">
                  {asset?.rackLocations?.map((rl, index) => (
                    <Typography key={index}>{formatRackLocationLabel(rl)}</Typography>
                  ))}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          {hasMobileLocation && <MobileLocationWarning />}
        </DialogContent>
        <DialogActions sx={{ p: 2, display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <MoveActions
            hasMobileLocation={hasMobileLocation}
            onOpenInventoryModal={() => setOpenInventoryModal(true)}
            onConsolidateMobileLocation={handleConsolidateMobileLocation}
            onMoveToOtherWarehouse={handleMoveToOtherWarehouse}
          />
        </DialogActions>
      </Dialog>
      {openMobileLocationModal && (
        <SelectMobileLocationModal
          onClose={handleMobileLocationClose}
          onOverride={handleMobileLocationOverride}
          onSubmit={handleMobileLocationSubmit}
        />
      )}
    </>
  );
}
